import { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../app/hooks'
import { fetchPOIById } from '../../features/poi/poiThunks'
import { generateActions } from '../../shared/utils/actions.utils'
import { clearActivePOI } from '../../features/poi/poiSlice'
import { getFilePath } from '../../shared/utils'
import { parentMeta } from '../content/ContentBlock/type'

interface UsePOIProps {
  poiId: string
}

const usePOI = (props: UsePOIProps) => {
  const { poiId } = props
  const dispatch = useDispatch()

  // =======================
  // Selectors
  // =======================
  const { data: zoneData } = useAppSelector((state) => state.zone)
  const { active } = useAppSelector((state) => state.poi)
  const {
    data: activePOIData,
    loading: activePOILoading,
    error: activePOIError,
  } = active
  const activePOIScenes = activePOIData?.scene?.scenes ?? {}

  // =======================
  // Effects
  // =======================
  useEffect(() => {
    if (poiId) {
      dispatch(fetchPOIById(poiId))
    }
    return () => {
      // clear poi on unmount
      dispatch(clearActivePOI())
    }
  }, [dispatch, poiId])

  // =======================
  // Props
  // =======================
  const loading = activePOILoading || !activePOIData?.scene?.id || !zoneData?.id
  const itemsFound = Object.keys({ ...activePOIData?.scene?.items }).length
    ? true
    : false
  const actions = useMemo(() => {
    return generateActions({ sceneMap: activePOIScenes })
  }, [activePOIScenes])

  const content = useMemo(() => {
    if (!zoneData?.id || !activePOIData?.scene?.id) return null
    return {
      filePath: getFilePath({
        filePath: activePOIData.scene?.filePath,
        zoneId: zoneData.id,
        sceneId: activePOIData.scene.id,
      }),
      parentMeta: {
        id: activePOIData?.scene?.id,
        title: activePOIData?.scene?.title,
      } as parentMeta,
    }
  }, [zoneData?.id, activePOIData?.scene?.id])

  return {
    poi: activePOIData,
    itemsFound,
    zoneId: zoneData?.id,
    actions,
    content,
    loading,
    error: activePOIError,
  }
}

export default usePOI
